import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { catchError, map, Observable, Subscription, take } from 'rxjs';
import { Store } from '@ngrx/store';

import { LanguageService } from '@fcom/ui-translate';
import { FragmentService, SelectOption } from '@fcom/common';
import { AppState, ConfigService, CountryLanguage, CountryState } from '@fcom/core';
import { EMAIL_PATTERN, NAME_PATTERN, unsubscribe } from '@fcom/core/utils';
import { shownConfs } from '@fcom/core/reducers/langs';
import { ButtonTheme, LoaderTheme, NotificationTheme } from '@fcom/ui-components';
import { FormStatus } from '@fcom/service-forms';
import { profileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors';
import { CmsArticle } from '@fcom/core-api';

import { NewsletterSignupService } from '../../../services/newsletter-signup/newsletter-signup.service';

@Component({
  selector: 'fin-newsletter-signup-form',
  templateUrl: './newsletter-signup-form.component.html',
  styleUrls: ['./newsletter-signup-form.component.scss'],
})
export class NewsletterSignupFormComponent implements OnInit, OnDestroy {
  public readonly FormStatus = FormStatus;

  readonly ButtonTheme = ButtonTheme;
  readonly LoaderTheme = LoaderTheme;
  readonly NotificationTheme = NotificationTheme;

  reactiveForm: UntypedFormGroup;
  countries: SelectOption[];
  languages: SelectOption[];
  formStatus: FormStatus = FormStatus.INITIAL;
  isLoggedInUser$: Observable<boolean>;
  errorArticle$: Observable<CmsArticle>;
  enableNewsletterSignUpForm = false;

  private countryLanguageList: CountryState[];
  private subscriptions: Subscription = new Subscription();

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private newsletterService: NewsletterSignupService,
    private fragmentService: FragmentService,
    private configService: ConfigService,
    private store$: Store<AppState>,
    private languageService: LanguageService
  ) {}

  ngOnInit(): void {
    this.countryLanguageList = shownConfs.map((country) => ({
      ...country,
      languages: country.languages.map(
        (language): CountryLanguage => ({
          name: language.name,
          code: language.code.split('-').pop(),
        })
      ),
    }));

    this.isLoggedInUser$ = this.store$.pipe(
      profileOrUndefinedIfNotLoggedIn(),
      map((profile) => !!profile)
    );

    this.errorArticle$ = this.fragmentService
      .getFragment<CmsArticle>('fragments.newsletterSignUpForm.url')
      .pipe(take(1));

    this.enableNewsletterSignUpForm = this.configService.cfg.enableNewsletterSignUpForm;

    this.countries = this.createSelectOption(this.countryLanguageList);
    this.createForm();

    this.subscriptions.add(
      this.reactiveForm.get('country').valueChanges.subscribe((val) => {
        if (val) {
          this.reactiveForm.get('language').enable();
        } else {
          this.reactiveForm.get('language').disable();
        }
      })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  onSubmit(): void {
    if (!this.reactiveForm.valid) {
      return;
    }

    this.formStatus = FormStatus.LOADING;
    const country = this.reactiveForm.get('country').value;
    const singleLanguage = this.countryLanguageList.find((item) => item.code === country).languages[0]?.code;
    this.subscriptions.add(
      this.newsletterService
        .postSubscribeRequest({
          firstName: this.reactiveForm.get('firstName').value,
          lastName: this.reactiveForm.get('lastName').value,
          email: this.reactiveForm.get('email').value,
          country,
          language: ((this.reactiveForm.get('language').value as string) || singleLanguage || '').toUpperCase(),
          pageUrl: this.router.url,
        })
        .pipe(
          take(1),
          catchError((err: unknown) => {
            this.formStatus = FormStatus.INITIALIZING_ERROR;
            throw err;
          })
        )
        .subscribe((result) => {
          if (result?.message) {
            this.formStatus = FormStatus.COMPLETE;
          } else {
            this.formStatus = FormStatus.INITIALIZING_ERROR;
          }
        })
    );
  }

  countryChanged(countryCode: string): void {
    this.languages = this.createSelectOption(
      this.countryLanguageList.find((item) => item.code === countryCode).languages
    );

    if (this.languages.length === 1) {
      this.reactiveForm.controls['language'].clearValidators();
    } else {
      this.reactiveForm.controls['language'].setValidators([Validators.required]);
    }
    this.reactiveForm.controls['language'].updateValueAndValidity();
  }

  private createForm = (): void => {
    const [language, country] = (this.languageService.localeValue || '_').split('_');

    this.reactiveForm = this.fb.group({
      firstName: ['', Validators.compose([Validators.required, Validators.pattern(NAME_PATTERN)])],
      lastName: ['', Validators.compose([Validators.required, Validators.pattern(NAME_PATTERN)])],
      email: ['', Validators.compose([Validators.required, Validators.pattern(EMAIL_PATTERN)])],
      country: [country, Validators.required],
      language: [{ value: language, disabled: !country }, Validators.required],
    });

    if (country) {
      this.countryChanged(country);
    }
  };

  private createSelectOption = (object: CountryLanguage[]): SelectOption[] =>
    object.map((item: CountryLanguage) => ({ value: item.code, name: item.name }) as SelectOption);
}
